import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Instagram, Facebook, Twitter, Youtube, Globe, Phone, Mail, MessageCircle, Star, MapPin, ShieldCheck, Store, ChevronRight, Search, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { api, mediaUrl, formatMoney } from '@/lib/api';
import { AdCard } from '@/components/marketplace/AdCard';
import { ProBadge } from '@/components/common/ProBadge';
import { VerifiedBadge } from '@/components/common/VerifiedBadge';
import { PublicHeader } from '@/components/marketplace/PublicHeader';
import { useShareConfig } from '@/context/ShareConfigContext';

const THEMES = {
    minimal: {
        heroClass: 'bg-white',
        titleFont: 'font-serif',
        cardClass: 'bg-white',
        sectionBg: 'bg-white',
    },
    bold: {
        heroClass: 'bg-gradient-to-br from-red-600 via-red-500 to-black text-white',
        titleFont: 'font-black',
        cardClass: 'bg-white',
        sectionBg: 'bg-secondary/30',
    },
    editorial: {
        heroClass: 'bg-neutral-950 text-white',
        titleFont: 'font-serif italic',
        cardClass: 'bg-white',
        sectionBg: 'bg-white',
    },
};

const SocialIcon = ({ kind, value }) => {
    if (!value) return null;
    const icons = { instagram: Instagram, facebook: Facebook, twitter: Twitter, youtube: Youtube, website: Globe };
    const Icon = icons[kind] || Globe;
    let href = value;
    if (kind === 'instagram' && !value.startsWith('http')) href = `https://instagram.com/${value.replace('@','')}`;
    if (kind === 'facebook' && !value.startsWith('http')) href = `https://facebook.com/${value}`;
    if (kind === 'twitter' && !value.startsWith('http')) href = `https://twitter.com/${value.replace('@','')}`;
    if (kind === 'tiktok' && !value.startsWith('http')) href = `https://tiktok.com/@${value.replace('@','')}`;
    if (kind === 'whatsapp' && !value.startsWith('http')) href = `https://wa.me/${value.replace(/\D/g,'')}`;
    if (kind === 'youtube' && !value.startsWith('http')) href = `https://youtube.com/@${value.replace('@','')}`;
    return <a href={href} target="_blank" rel="noreferrer" aria-label={kind} className="p-2 hover:opacity-80 transition-opacity" data-testid={`storefront-social-${kind}`}><Icon className="h-5 w-5" /></a>;
};

export default function StorefrontPage() {
    const { nickname } = useParams();
    const nav = useNavigate();
    const { buildShareUrl } = useShareConfig();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [tab, setTab] = useState('all'); // all | collection-<slug> | about | policies | contact
    const [contactOpen, setContactOpen] = useState(false);
    const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
    const [contactBusy, setContactBusy] = useState(false);

    useEffect(() => {
        let m = true;
        (async () => {
            try {
                const { data: d } = await api.get(`/storefront/${nickname}`);
                if (m) setData(d);
            } catch (e) {
                if (m) setData({ __not_found: true });
            } finally { if (m) setLoading(false); }
        })();
        return () => { m = false; };
    }, [nickname]);

    const theme = THEMES[data?.storefront?.theme || 'minimal'];
    const primary = data?.storefront?.primary_color || '#dc2626';

    const filteredAds = useMemo(() => {
        if (!data?.ads) return [];
        if (!q) return data.ads;
        const lc = q.toLowerCase();
        return data.ads.filter(a => (a.title || '').toLowerCase().includes(lc) || (a.tags || []).some(t => t.toLowerCase().includes(lc)));
    }, [data, q]);

    const submitContact = async () => {
        if (!contactForm.email || !contactForm.name || contactForm.message.length < 5) { toast.error('Completa nombre, email y mensaje'); return; }
        setContactBusy(true);
        try {
            await api.post(`/storefront/${nickname}/contact`, contactForm);
            toast.success('Mensaje enviado, el vendedor te responderá pronto.');
            setContactOpen(false);
            setContactForm({ name: '', email: '', subject: '', message: '' });
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al enviar'); }
        finally { setContactBusy(false); }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando tienda…</div>;
    if (!data || data.__not_found || !data.storefront?.enabled) {
        return (
            <div className="min-h-screen bg-secondary/20">
                <PublicHeader />
                <div className="max-w-2xl mx-auto p-10 text-center">
                    <Store className="h-14 w-14 mx-auto text-muted-foreground" />
                    <h1 className="text-2xl font-semibold mt-3">Esta tienda aún no está activa</h1>
                    <p className="text-muted-foreground mt-2">El vendedor <strong>@{nickname}</strong> aún no ha activado su tienda personal. Puedes ver su perfil público.</p>
                    <Button className="mt-4 bg-red-600 hover:bg-red-700" onClick={() => nav(`/u/${nickname}`)}>Ver perfil público</Button>
                </div>
            </div>
        );
    }

    const s = data.storefront;
    const seller = data.seller;
    const currentCollection = tab.startsWith('collection-') ? data.collections.find(c => `collection-${c.slug}` === tab) : null;
    const adsToShow = currentCollection ? data.ads.filter(a => currentCollection.ad_ids.includes(a.id)) : filteredAds;

    return (
        <div className="min-h-screen bg-secondary/10" data-testid="storefront-page" style={{ '--sf-primary': primary }}>
            <PublicHeader />

            {/* Announcement bar (Phase 20g) */}
            {s.announcement_bar_enabled && s.announcement_bar_text && (
                <div
                    className="w-full text-center text-white text-xs sm:text-sm py-1.5 px-3"
                    style={{ backgroundColor: primary || '#dc2626' }}
                    data-testid="storefront-announcement-bar"
                >
                    {s.announcement_bar_link ? (
                        <a href={s.announcement_bar_link} target="_blank" rel="noopener noreferrer" className="hover:underline">{s.announcement_bar_text}</a>
                    ) : (
                        <span>{s.announcement_bar_text}</span>
                    )}
                </div>
            )}

            {/* Custom hero (Phase 20g) - takes priority over banner_url when set */}
            {s.hero_image_url && (
                <section className="relative" data-testid="storefront-custom-hero">
                    <div className="relative h-[45vh] min-h-[280px] max-h-[520px] w-full overflow-hidden">
                        <img src={s.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/45 to-black/70" />
                        <div className="relative z-10 h-full flex flex-col items-center justify-center text-white text-center px-4">
                            {s.hero_headline && (
                                <h2 className="text-3xl md:text-5xl font-bold tracking-tight drop-shadow-lg max-w-3xl">{s.hero_headline}</h2>
                            )}
                            {s.hero_cta_label && (
                                <a
                                    href={s.hero_cta_link || `#`}
                                    className="mt-5 inline-flex items-center gap-2 rounded-xl bg-white text-red-700 hover:bg-red-50 px-6 py-3 font-semibold shadow-lg transition-colors"
                                    data-testid="storefront-hero-cta"
                                >
                                    {s.hero_cta_label} <ArrowRight className="h-4 w-4" />
                                </a>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* Hero / Banner */}
            <section className={`relative ${theme.heroClass}`} data-testid="storefront-hero">
                {s.banner_url && (
                    <div className="absolute inset-0 opacity-40">
                        <img src={s.banner_url} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                )}
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10 md:py-16">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
                        {s.logo_url ? (
                            <img src={s.logo_url} alt={s.display_name} className="h-20 w-20 md:h-28 md:w-28 rounded-2xl border-4 border-white bg-white object-cover shadow-lg" />
                        ) : (
                            <div className="h-20 w-20 md:h-28 md:w-28 rounded-2xl border-4 border-white bg-white flex items-center justify-center shadow-lg">
                                <Store className="h-10 w-10 text-red-600" />
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className={`${theme.titleFont} text-3xl md:text-5xl tracking-tight`} data-testid="storefront-name">{s.display_name || seller.nickname}</h1>
                                <VerifiedBadge verified={seller.verified} />
                                {data.pro_plan && <ProBadge userId={seller.id} plan={data.pro_plan} size="lg" />}
                            </div>
                            {s.tagline && <p className="text-lg md:text-xl mt-2 opacity-90" data-testid="storefront-tagline">{s.tagline}</p>}
                            <div className="flex items-center gap-3 mt-3 flex-wrap text-sm opacity-80">
                                <span className="flex items-center gap-1"><Star className="h-4 w-4 text-amber-400 fill-amber-400" />{seller.rating_avg?.toFixed(1) || '0.0'} <span className="opacity-70">({seller.rating_count || 0} reviews)</span></span>
                                {seller.location && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{seller.location}</span>}
                                <span>@{seller.nickname}</span>
                            </div>
                            <div className="flex items-center gap-1 mt-2">
                                {s.socials?.instagram && <SocialIcon kind="instagram" value={s.socials.instagram} />}
                                {s.socials?.facebook && <SocialIcon kind="facebook" value={s.socials.facebook} />}
                                {s.socials?.twitter && <SocialIcon kind="twitter" value={s.socials.twitter} />}
                                {s.socials?.tiktok && <SocialIcon kind="tiktok" value={s.socials.tiktok} />}
                                {s.socials?.youtube && <SocialIcon kind="youtube" value={s.socials.youtube} />}
                                {s.socials?.whatsapp && <SocialIcon kind="whatsapp" value={s.socials.whatsapp} />}
                                {s.socials?.website && <SocialIcon kind="website" value={s.socials.website} />}
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 md:items-end">
                            <Button size="lg" onClick={() => setContactOpen(true)} className="bg-red-600 hover:bg-red-700" data-testid="storefront-contact-btn"><MessageCircle className="h-4 w-4 mr-2" />Contactar</Button>
                            <Button variant="outline" onClick={() => nav(`/u/${seller.nickname}`)}>Ver perfil</Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Nav tabs */}
            <div className="sticky top-14 z-30 bg-white/95 backdrop-blur border-b">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center gap-1 overflow-x-auto">
                    <button onClick={() => setTab('all')} className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === 'all' ? 'border-red-600 text-red-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="storefront-tab-all">Todos ({data.ads_total})</button>
                    {data.collections?.filter(c => c.active).map(c => (
                        <button key={c.id} onClick={() => setTab(`collection-${c.slug}`)} className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === `collection-${c.slug}` ? 'border-red-600 text-red-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid={`storefront-tab-${c.slug}`}>{c.name} ({c.ad_ids?.length || 0})</button>
                    ))}
                    {s.about && <button onClick={() => setTab('about')} className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === 'about' ? 'border-red-600 text-red-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="storefront-tab-about">Sobre nosotros</button>}
                    {(s.policies_shipping || s.policies_returns || s.policies_privacy) && <button onClick={() => setTab('policies')} className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === 'policies' ? 'border-red-600 text-red-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="storefront-tab-policies">Políticas</button>}
                    {s.faq?.length > 0 && <button onClick={() => setTab('faq')} className={`px-3 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === 'faq' ? 'border-red-600 text-red-600' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="storefront-tab-faq">FAQ</button>}
                </div>
            </div>

            {/* Content */}
            <main className={`${theme.sectionBg} min-h-[400px]`}>
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
                    {(tab === 'all' || tab.startsWith('collection-')) && (
                        <>
                            {tab === 'all' && (
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="relative flex-1 max-w-md">
                                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar en la tienda…" className="pl-9" data-testid="storefront-search" />
                                    </div>
                                </div>
                            )}
                            {currentCollection?.description && (
                                <p className="text-muted-foreground mb-4" data-testid="storefront-collection-desc">{currentCollection.description}</p>
                            )}
                            {adsToShow.length === 0 ? (
                                <div className="text-center py-16 text-muted-foreground">Sin productos por ahora.</div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {adsToShow.map(a => <AdCard key={a.id} ad={a} />)}
                                </div>
                            )}
                        </>
                    )}
                    {tab === 'about' && s.about && (
                        <Card><CardContent className="p-6 md:p-8 prose max-w-none whitespace-pre-wrap" data-testid="storefront-about">{s.about}</CardContent></Card>
                    )}
                    {tab === 'policies' && (
                        <div className="space-y-4">
                            {s.policies_shipping && <Card><CardContent className="p-6" data-testid="storefront-policy-shipping"><h3 className="font-semibold mb-2">Envíos</h3><p className="whitespace-pre-wrap text-sm text-muted-foreground">{s.policies_shipping}</p></CardContent></Card>}
                            {s.policies_returns && <Card><CardContent className="p-6" data-testid="storefront-policy-returns"><h3 className="font-semibold mb-2">Devoluciones</h3><p className="whitespace-pre-wrap text-sm text-muted-foreground">{s.policies_returns}</p></CardContent></Card>}
                            {s.policies_privacy && <Card><CardContent className="p-6" data-testid="storefront-policy-privacy"><h3 className="font-semibold mb-2">Privacidad</h3><p className="whitespace-pre-wrap text-sm text-muted-foreground">{s.policies_privacy}</p></CardContent></Card>}
                        </div>
                    )}
                    {tab === 'faq' && s.faq?.length > 0 && (
                        <div className="space-y-3" data-testid="storefront-faq">
                            {s.faq.map((item, i) => (
                                <Card key={i}><CardContent className="p-5"><h4 className="font-semibold mb-2">{item.q}</h4><p className="text-sm text-muted-foreground whitespace-pre-wrap">{item.a}</p></CardContent></Card>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t bg-white py-6 mt-4">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                    <div>© {new Date().getFullYear()} {s.display_name || `@${seller.nickname}`}</div>
                    {s.show_powered_by !== false && (
                        <Link to="/" className="flex items-center gap-1 hover:text-red-600" data-testid="storefront-powered-by">
                            <Store className="h-3 w-3" />Powered by Ok te lo vendo
                        </Link>
                    )}
                </div>
            </footer>

            {/* Contact dialog */}
            <Dialog open={contactOpen} onOpenChange={setContactOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Contactar a {s.display_name || `@${seller.nickname}`}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Tu nombre</Label><Input value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} data-testid="contact-name" /></div>
                        <div><Label>Email</Label><Input type="email" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} data-testid="contact-email" /></div>
                        <div><Label>Asunto (opcional)</Label><Input value={contactForm.subject} onChange={e => setContactForm({ ...contactForm, subject: e.target.value })} data-testid="contact-subject" /></div>
                        <div><Label>Mensaje</Label><Textarea rows={4} value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })} data-testid="contact-message" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setContactOpen(false)}>Cancelar</Button>
                        <Button onClick={submitContact} disabled={contactBusy} className="bg-red-600 hover:bg-red-700" data-testid="contact-submit">{contactBusy ? '…' : 'Enviar mensaje'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
